import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { getSiteUrl } from '../../utils/siteUrl';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Note: Don't include full path in redirectTo - Supabase appends the hash fragment
      // Just use the root URL, Supabase will add recovery parameters
      const redirectUrl = `${window.location.origin}/update-password`;
      console.log('🔐 Password Reset: Sending email with redirectTo:', redirectUrl);
      console.log('   Current origin:', window.location.origin);
      console.log('   Full request URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('❌ Password Reset Error:', error);
        throw error;
      }

      console.log('✅ Password reset email sent successfully to:', email);
      setEmailSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                {/* Inline SVG to avoid arc path parsing issues */}
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" ry="2"></rect>
                  <polyline points="3,7 12,13 21,7"></polyline>
                </svg>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-foreground text-center mb-3">
              Check Your Email
            </h2>
            
            <p className="text-muted-foreground text-center mb-6">
              We've sent a password reset link to <strong className="text-foreground">{email}</strong>
            </p>
            
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-semibold text-foreground mb-2">What's next?</p>
              <ol className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">1.</span>
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">2.</span>
                  <span>Click the password reset link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">3.</span>
                  <span>Create your new password</span>
                </li>
              </ol>
            </div>
            
            <div className="space-y-3">
              <Button
                fullWidth
                variant="outline"
                onClick={() => navigate('/authentication')}
              >
                Back to Login
              </Button>
              
              <Button
                fullWidth
                variant="ghost"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
              >
                Try Different Email
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Lock" size={32} color="var(--color-primary)" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-foreground text-center mb-3">
            Reset Your Password
          </h2>
          
          <p className="text-muted-foreground text-center mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              error={error}
              required
              autoFocus
            />
            
            <Button
              type="submit"
              variant="default"
              loading={loading}
              fullWidth
              iconName="Send"
              iconPosition="right"
            >
              Send Reset Link
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => navigate('/authentication')}
            >
              Back to Login
            </Button>
          </form>
          
          <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="text-primary mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Secure Process</p>
                <p>The reset link will expire in 60 minutes for your security.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
